// AI Generated Component
import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SessionService } from '@monsido/core/session/session.service';
import { QALineChartService } from '@client/app/blocks/service/charts/qa-line-chart/qa-line-chart.service';
import { ChangesHistoryService } from '@client/app/pages/global/changes-history/services/changes-history/changes-history.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { ScoresService } from '@client/app/modules/score/services/scores/scores.service';
import { DemoHasAccessService } from '@client/app/modules/demodata/services/demo-has-access.service';
import { PropertiesSumService } from '@client/app/filters/properties-sum.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';

@Component({
    selector: 'last-scan',
    standalone: false,
    template: `
        <div class="card panel-monsido-card" *ngIf="!hideCharts">
            <div class="card-header">
                <h2 class="font-weight-300 font-size-1-5 mb-1" translate>
                    Quality Assurance Diagnostics
                </h2>
                <span class="text-grey" translate>Percentage shows number of pages that are compliant with all QA checks</span>
            </div>
            <div class="card-body">
                <mon-spinner [progress]="progress">
                    <mon-chart-dashboard
                        *ngIf="loaded"
                        [doughnutLabels]="doughnutLabels"
                        [doughnutData]="doughnutData"
                        [doughnutColors]="doughnutColors"
                        [doughnutOptions]="doughnutOptions"
                        [lineLabels]="lineChartLabels"
                        [lineData]="lineChartData"
                        [lineSeries]="lineChartSeries"
                        [lineColors]="lineColors"
                        [lineOptions]="lineOptions"
                        [lineOverride]="lineOverride"
                        [verticalData]="verticalData"
                        [cards]="cardsList">
                    </mon-chart-dashboard>
                </mon-spinner>
            </div>
            <div class="card-footer bg-white text-right" *ngIf="showHistory">
                <a class="btn-link" uiSref="base.customer.domain.changesHistory" [uiParams]="historyCenterParameters" translate>
                    Show history
                </a>
            </div>
        </div>
    `,
})
export class LastScanComponent implements OnInit {
    @Input() qaSummary: any;
    @Input() history: any;
    @Input() progress: any;

    historyCenterParameters: any = {};
    allowVerticalAverage = false;
    loaded = false;
    domain: any;
    cardsList: any[] = [];
    hideCharts!: boolean;
    showHistory!: boolean;
    doughnutOptions: any = {};
    doughnutData: any[] = [];
    doughnutLabels: any[] = [];
    doughnutColors: any[] = [];
    lineOverride: any = [];
    lineChartSeries: any[] = [];
    lineColors: any[] = [];
    lineOptions: any = [];
    lineChartLabels: any[] = [];
    lineChartData: any[] = [];
    latestCrawl: any;
    verticalData: any;

    constructor (
        private translateService: TranslateService,
        private sessionService: SessionService,
        private qaLineChartService: QALineChartService,
        private changesHistoryService: ChangesHistoryService,
        private activeFeatureService: ActiveFeatureService,
        private scoresService: ScoresService,
        private demoHasAccessService: DemoHasAccessService,
        private propertiesSumService: PropertiesSumService,
        private pipeService: PipeService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    activate (): void {
        this.domain = this.sessionService.domain;
        this.cardsList = [];
        this.historyCenterParameters = this.changesHistoryService.getHistoryCenterParameters('qa');
        this.hideCharts =
            !this.activeFeatureService.isFeatureActivePure('qa_spellcheck') || !this.activeFeatureService.isFeatureActivePure('qa_links');
        this.showHistory = this.activeFeatureService.isFeatureActivePure('history_center_all');
        this.doughnutOptions = {};
        this.doughnutData = [];
        this.doughnutLabels = [];
        this.doughnutColors = [];

        this.lineOverride = [];
        this.lineChartSeries = [];
        this.lineColors = [];
        this.lineOptions = [];
        this.lineChartLabels = [];
        this.lineChartData = [];

        this.progress.then(() => {
            setTimeout(() => {
                this.latestCrawl = this.getLatestHistory();
                this.setupDoughnutChart();
                this.setupLineChart();
                this.setupNotifications();

                const isDemoAccount = this.demoHasAccessService.isDemoAccount();
                if (isDemoAccount) {
                    this.allowVerticalAverage = true;
                    this.setVerticalData(this.scoresService.getQADemoData());
                } else {
                    this.allowVerticalAverage = this.activeFeatureService.isFeatureActive('monsido_score');
                    if (this.allowVerticalAverage) {
                        this.scoresService.getAverageData('qa').then((data: any) => this.setVerticalData(data));
                    }
                }
                this.loaded = true;
            });
        }, () => {});
    }

    getLatestHistory (): any {
        if (this.history.length > 0) {
            return this.history.sort((a: any, b: any) => {
                // Get the latest history id
                return a.id > b.id ? -1 : a.id < b.id ? 1 : 0;
            })[0];
        } else {
            return {};
        }
    }

    setupLineChart (): void {
        const lineChartService = this.qaLineChartService.createQaLineChartService();
        this.lineOverride = lineChartService.getOverride();
        this.lineChartSeries = lineChartService.getSeries();
        this.lineColors = lineChartService.getColors();
        this.lineOptions = lineChartService.getOption();
        this.lineChartLabels = lineChartService.getLabels(this.history);
        this.lineChartData = lineChartService.getData(this.history);
    }

    setupDoughnutChart (): void {
        const pagesStack = ['pages_with_quality_notifications', 'pages_without_quality_notifications'];
        const documentsStack = ['documents_with_quality_notifications', 'documents_without_quality_notifications'];

        const pagesCount = this.qaSummary.pages_with_quality_notifications || 0;
        const documentsCount = this.qaSummary.documents_with_quality_notifications || 0;

        const totalPagesCount = this.propertiesSumService.sum(this.qaSummary, pagesStack);
        const totalDocumentsCount = this.propertiesSumService.sum(this.qaSummary, documentsStack);
        const totalContent = totalPagesCount + totalDocumentsCount;
        const totalErrors = pagesCount + documentsCount;

        const percentage = (totalErrors / totalContent) * 100;

        this.doughnutOptions = {
            centerText: {
                percentageLabel: this.pipeService.get('monNumber')((100 - percentage) / 100, '0.[00] %'),
                totalLabel: this.translateService.getString('Content Quality'),
                subLabel: this.translateService.getString('Compliance'),
                subFontSize: 170,
                totalFontSize: 170,
            },
            cutoutPercentage: 80,
            tooltips: {
                callbacks: {
                    beforeLabel: () => {},
                    label: (tooltipItem: any) => {
                        let tooltipPagesCount = this.qaSummary.pages_with_quality_notifications || 0;
                        let tooltipDocumentsCount = this.qaSummary.documents_with_quality_notifications || 0;
                        if (tooltipItem.index === 0) {
                            // First entry is expected to be entries without errors
                            tooltipPagesCount = (this.latestCrawl.page_count || 0) - tooltipPagesCount;
                            tooltipDocumentsCount = (this.latestCrawl.documents_count || 0) - tooltipDocumentsCount;
                        }

                        return this.getTooltipLabelText(tooltipPagesCount, tooltipDocumentsCount);
                    },
                },
            },
        };
        this.doughnutData = [totalContent - totalErrors, totalErrors];
        this.doughnutLabels = [this.translateService.getString('Content in compliance'), this.translateService.getString('Content with issues')];
        this.doughnutColors = ['#4dc0bd', '#DCDCDC'];
    }

    setupNotifications (): void {
        const summaryIssues = this.propertiesSumService.sum(this.qaSummary, ['confirmed_misspellings', 'broken_images', 'broken_links']);
        const summaryPageIssues = this.propertiesSumService.sum(this.qaSummary, [
            'pages_with_quality_notifications',
            'documents_with_quality_notifications',
        ]);
        const historyIssues = this.propertiesSumService.sum(this.history[this.history.length - 1], [
            'spelling_errors_confirmed_count',
            'dead_count',
        ]);
        const historyPageIssues = this.propertiesSumService.sum(this.history[this.history.length - 1], ['pages_with_notifications_count']);

        this.cardsList = [
            {
                title: this.translateService.getString('Total QA issues'),
                tooltipTitle: this.translateService.getString('Contains confirmed misspellings, broken links & images'),
                issues: summaryIssues,
                change: this.history.length > 1 ? summaryIssues - historyIssues : 0,
                changePercentage: this.getPercentageChange(summaryIssues, historyIssues),
            },
            {
                title: this.translateService.getString('Content with issues'),
                issues: summaryPageIssues,
                change: this.history.length > 1 ? summaryPageIssues - historyPageIssues : 0,
                changePercentage: this.getPercentageChange(summaryPageIssues, historyPageIssues),
            },
        ];
    }

    getTooltipLabelText (pages: number, documents: number): string {
        pages = pages || 0;
        documents = documents || 0;
        const scope = {
            pages: this.pipeService.get('monNumber')(pages),
            documents: this.pipeService.get('monNumber')(documents),
        };
        let single = '1 page';
        let multi = '[[pages]] pages';
        if (this.domain.scan.scan_documents) {
            single += ' and [[documents]] document(s)';
            multi += ' and [[documents]] document(s)';
        }
        return this.pipeService.get('translateWithPlural')(single, pages, multi, scope);
    }

    getPercentageChange (a: number, b: number): number {
        const difference = Math.abs(a - b);
        const average = (a + b) / 2;

        if (difference >= average) {
            return 100;
        }
        return (difference / average) * 100;
    }

    setVerticalData (data: any): void {
        this.verticalData = {
            verticalAverage: data.score,
            averageTitle: this.scoresService.getDefaultTitle(),
            tooltipText: this.scoresService.getDefaultTooltip() + ' ' + this.scoresService.getVerticalName(data.name),
        };
    }
}
